import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import { useLang } from "@/i18n";
import { useDiscovery } from "@/components/Layout";
import { MaskedLines, Reveal, FadeIn, ParallaxImage, Kicker } from "@/components/motion";
import { ARTICLES } from "@/articles";

export default function Article() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const openDiscovery = useDiscovery();
  const article = ARTICLES.find((a) => a.slug === slug);

  if (!article) return <Navigate to="/insights" replace />;

  const c = article[lang];
  const s = t.insights;

  return (
    <>
      <section className="relative overflow-hidden pt-32 pb-14 sm:pt-40 sm:pb-16 grain" data-testid="article-hero">
        <div className="pointer-events-none absolute -left-40 top-0 h-[420px] w-[420px] rounded-full bg-sage-100/70 blur-3xl" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <FadeIn delay={0.05}>
            <Link
              to="/insights"
              className="mb-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-navy-900/50 transition-colors hover:text-navy-900"
              data-testid="article-back"
            >
              <ArrowLeft size={13} /> {s.backToList}
            </Link>
          </FadeIn>
          <FadeIn delay={0.1}>
            <Kicker>{c.category}</Kicker>
          </FadeIn>
          <MaskedLines
            className="mt-9 max-w-4xl font-serif text-4xl leading-[1.08] tracking-tight text-navy-900 sm:text-5xl"
            delay={0.25}
            lines={[c.title]}
          />
          <Reveal delay={0.55}>
            <p className="mt-6 max-w-2xl font-serif text-lg italic leading-relaxed text-navy-900/60 sm:text-xl" data-testid="article-subtitle">
              {c.subtitle}
            </p>
          </Reveal>
          <Reveal delay={0.65}>
            <p className="mt-6 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-navy-900/40" data-testid="article-readtime">
              <Clock size={12} /> {article.readTime} {s.readTime}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8" data-testid="article-cover">
        <Reveal delay={0.1}>
          <ParallaxImage src={article.image} alt={c.title} testId="article-cover-image" className="aspect-[21/9] w-full" speed={0.1} />
        </Reveal>
      </section>

      <section className="py-16 sm:py-20" data-testid="article-body">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <p className="text-lg leading-relaxed text-navy-900/85 sm:text-xl sm:leading-relaxed" data-testid="article-intro">
              {c.intro}
            </p>
          </Reveal>
          {c.sections.map((sec, i) => (
            <Reveal key={i} delay={0.05}>
              <div className="mt-14" data-testid={`article-section-${i}`}>
                <h2 className="font-serif text-2xl tracking-tight text-navy-900 sm:text-3xl">{sec.heading}</h2>
                <span className="mt-4 block h-px w-14 bg-gold" />
                <p className="mt-6 text-base leading-relaxed text-navy-900/75">{sec.body}</p>
              </div>
            </Reveal>
          ))}

          <Reveal delay={0.05}>
            <div className="mt-16 border border-navy-900/10 bg-sand p-7 sm:p-9" data-testid="article-sources">
              <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-gold-deep">{s.sourcesLabel}</p>
              <ul className="mt-4 space-y-2.5">
                {c.sources.map((src, i) => (
                  <li key={i} className="flex gap-3 text-sm leading-relaxed text-navy-900/65" data-testid={`article-source-${i}`}>
                    <span className="mt-[9px] h-px w-4 shrink-0 bg-gold-deep/60" />
                    {src}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-navy-900 py-16 sm:py-20" data-testid="article-cta">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <Reveal>
            <p className="font-serif text-2xl leading-snug tracking-tight text-cream sm:text-3xl" data-testid="article-cta-text">
              {c.cta}
            </p>
            <button
              onClick={openDiscovery}
              className="group mt-9 inline-flex items-center gap-3 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-navy-950 transition-colors duration-300 hover:bg-gold-light"
              data-testid="article-cta-btn"
            >
              {c.ctaButton}
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>
        </div>
      </section>
    </>
  );
}
